import React, { useEffect, useRef, useState } from "react";
import grapesjs from "grapesjs";
import newsletterPreset from "grapesjs-preset-newsletter";
import { Loader2 } from "lucide-react";
import "grapesjs/dist/css/grapes.min.css";

// El preset solo trae etiquetas en inglés — se traducen a mano por id de
// bloque (ids documentados en grapesjs-preset-newsletter/README.md).
const BLOCK_LABELS = {
  sect100: "1 columna",
  sect50: "2 columnas",
  sect30: "3 columnas",
  sect37: "2 columnas (30/70)",
  button: "Botón",
  divider: "Línea divisoria",
  text: "Texto",
  "text-sect": "Título + texto",
  image: "Imagen",
  quote: "Cita",
  link: "Enlace",
  "link-block": "Bloque con enlace",
  "grid-items": "Cuadrícula",
  "list-items": "Lista",
};

// Editor visual de arrastrar y soltar para el contenido de una campaña — la
// alternativa a que la persona tenga que escribir HTML a mano. Se monta a
// pantalla completa porque grapesjs necesita bastante espacio real para los
// paneles de bloques/estilos/capas más el lienzo.
const EmailTemplateEditor = ({
  open,
  onClose,
  onSave,
  initialHtml,
  initialDesignJson,
}) => {
  const containerRef = useRef(null);
  const editorRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!open || !containerRef.current) return undefined;

    const editor = grapesjs.init({
      container: containerRef.current,
      height: "100%",
      width: "auto",
      fromElement: false,
      storageManager: false,
      plugins: [newsletterPreset],
      pluginsOpts: {
        [newsletterPreset]: {
          modalTitleImport: "Importar HTML",
          modalTitleExport: "Código HTML",
          modalBtnImport: "Importar",
        },
      },
    });

    editorRef.current = editor;

    Object.entries(BLOCK_LABELS).forEach(([id, label]) => {
      const block = editor.BlockManager.get(id);
      if (block) block.set("label", label);
    });

    // Un diseño guardado siempre gana sobre el HTML plano: reabrir desde el
    // HTML final perdería los bloques/estilos originales. El HTML plano solo
    // se usa como arranque para campañas creadas antes de este editor.
    if (initialDesignJson) {
      try {
        editor.loadProjectData(JSON.parse(initialDesignJson));
      } catch (err) {
        if (initialHtml) editor.setComponents(initialHtml);
      }
    } else if (initialHtml) {
      editor.setComponents(initialHtml);
    }

    setReady(true);

    return () => {
      editor.destroy();
      editorRef.current = null;
      setReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSave = () => {
    const editor = editorRef.current;
    if (!editor) return;
    const html = editor.runCommand("gjs-get-inlined-html");
    const designJson = JSON.stringify(editor.getProjectData());
    onSave({ html, designJson });
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex flex-col bg-white dark:bg-slate-900">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700 shrink-0">
        <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          Editor de plantilla
        </h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="px-4 py-1.5 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Guardar diseño
          </button>
        </div>
      </div>
      <div className="flex-1 min-h-0 relative">
        {!ready && (
          <div className="absolute inset-0 flex items-center justify-center bg-white dark:bg-slate-900">
            <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
          </div>
        )}
        <div ref={containerRef} className="h-full" />
      </div>
    </div>
  );
};

export default EmailTemplateEditor;
